import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ALL_TYPES, type CreativeType } from "@acs/shared";
import { api } from "../api";

const ACCENT = "#5E6AD2";

type TreeSummary = { winners: number; losers: number; qa_fails: number; total: number };
type TreeNode = { label: string; level: string; summary: TreeSummary; children: TreeNode[]; executions: any[] };
const TREE_LEVEL_LABEL: Record<string, string> = { angle: "FEATURE", target_group: "AUDIENCE", message: "MESSAGE" };

function CompactTreeSection({ productId }: { productId: string }) {
  const navigate = useNavigate();
  const [tree, setTree] = useState<TreeNode | null>(null);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    api.getIdeaTree(productId).then((r) => setTree(r.tree as any)).catch(() => {}).finally(() => setLoading(false));
  }, [productId]);

  return (
    <section className="bg-white border rounded p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold">🌳 Kreatif Ağacı</h3>
        <button
          onClick={() => navigate(`/products/${productId}/tree`)}
          className="text-xs font-medium"
          style={{ color: ACCENT }}
        >
          Tam ekranda aç →
        </button>
      </div>
      {loading ? (
        <p className="text-xs text-zinc-400">Yükleniyor…</p>
      ) : !tree || tree.children.length === 0 ? (
        <p className="text-xs text-zinc-400">Henüz veri yok — bir kreatif ürettiğinde burada görünecek.</p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {tree.children.map((f, i) => {
            const open = openId === `${i}`;
            return (
              <div key={i} className="border border-zinc-100 rounded-lg overflow-hidden">
                <button
                  onClick={() => setOpenId(open ? null : `${i}`)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-zinc-50"
                >
                  <span className={`text-[10px] text-zinc-400 transition-transform ${open ? "rotate-90" : ""}`}>▶</span>
                  <span
                    className="text-[9.5px] font-bold rounded px-1.5 py-0.5"
                    style={{ color: ACCENT, background: `color-mix(in srgb, ${ACCENT} 10%, #fff)` }}
                  >{TREE_LEVEL_LABEL[f.level] ?? "FEATURE"}</span>
                  <span className="text-xs font-semibold text-zinc-800">{f.label}</span>
                  <span className="ml-auto text-[11px] text-zinc-400">{f.summary.total} üretildi</span>
                  {f.summary.winners > 0 && <span className="text-[10px] font-semibold text-emerald-600">🏆 {f.summary.winners}</span>}
                </button>
                {open && (
                  <div className="px-3 pb-2 pl-7 flex flex-col gap-1">
                    {f.children.map((c, j) => (
                      <div key={j} className="flex items-center gap-2 text-xs text-zinc-600 py-1">
                        <span className="text-[9px] font-bold text-zinc-400">{TREE_LEVEL_LABEL[c.level] ?? c.level.toUpperCase()}</span>
                        <span className="font-medium">{c.label}</span>
                        <span className="ml-auto text-[10.5px] text-zinc-400">{c.summary.total} üretildi</span>
                      </div>
                    ))}
                    {f.children.length === 0 && <span className="text-[11px] text-zinc-300">Alt kırılım yok</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

const API_ORIGIN = (import.meta.env.VITE_API_BASE ?? "/api")
  .replace(/\/$/, "").replace(/\/api$/, "");

import { BRAND_QUESTIONS } from "../lib/brandQuestions";
export { BRAND_QUESTIONS };

export function ProductSettings({ productId }: { productId: string }) {
  const [product, setProduct] = useState<any | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [types, setTypes] = useState<CreativeType[]>([]);
  const [features, setFeatures] = useState<{ id: string; name: string; pain_points: string[]; audiences: string[] }[]>([]);
  const [newFeatureName, setNewFeatureName] = useState("");
  const [expandedFeature, setExpandedFeature] = useState<string | null>(null);
  const [driveFolderId, setDriveFolderId] = useState("");
  const [savingDrive, setSavingDrive] = useState(false);
  const [hookDriveFolderId, setHookDriveFolderId] = useState("");
  const [bodyDriveFolderId, setBodyDriveFolderId] = useState("");
  const [savingHookDrive, setSavingHookDrive] = useState(false);
  const [savingBodyDrive, setSavingBodyDrive] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Brand Guidelines
  const [brandGuidelines, setBrandGuidelines] = useState("");
  const [savingBrand, setSavingBrand] = useState(false);
  const [onboardingAnswers, setOnboardingAnswers] = useState<Record<string, string>>({});
  const [expandedBrandQ, setExpandedBrandQ] = useState<string | null>(null);
  const [generatingBrand, setGeneratingBrand] = useState(false);

  // Subtitle vocabulary
  const [vocabulary, setVocabulary] = useState<{ word: string; replaces: string[] }[]>([]);
  const [savingVocab, setSavingVocab] = useState(false);
  const [subtitlePreset, setSubtitlePreset] = useState("");
  const [subtitleHighlightColor, setSubtitleHighlightColor] = useState("");
  const [subtitlePosition, setSubtitlePosition] = useState("");

  // Custom types
  const [customTypes, setCustomTypes] = useState<any[]>([]);
  const imageCustomTypes = customTypes.filter((ct) => ct.kind !== "video");
  const videoCustomTypes = customTypes.filter((ct) => ct.kind === "video");

  // UGC Scenarios (read-only list — adding is done via Creative Builder)
  type UgcScenario = {
    id: string; name: string; description: string; avatar_description?: string;
    location?: string | null; environment?: string | null; music?: string | null;
    cam_angle?: string | null; cam_movement?: string | null; emotion?: string | null;
    role_archetype?: string | null; season?: string | null;
  };
  const [ugcScenarios, setUgcScenarios] = useState<UgcScenario[]>([]);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  // Senaryo alt kırılım editörü (location/environment/music/cam angle/cam movement/emotion)
  const [scenarioEditor, setScenarioEditor] = useState<UgcScenario | null>(null);
  const [scenarioDraft, setScenarioDraft] = useState<Record<string, string>>({});
  const [savingScenario, setSavingScenario] = useState(false);
  // Prompt popup
  const [promptPopup, setPromptPopup] = useState<{ name: string; prompt: string } | null>(null);

  const [costs, setCosts] = useState<{ lang_breakdown?: Record<string, number>;
    grand_total_usd: number;
    pillars: Record<string, { count: number; unit: string; cost_usd: number }>;
  } | null>(null);
  const navigate = useNavigate();

  async function reload() {
    try {
      const [p, c] = await Promise.all([
        api.getProduct(productId),
        api.getProductCosts(productId).catch(() => null),
      ]);
      setProduct(p.product);
      setDriveFolderId(p.product.drive_folder_id ?? "");
      setHookDriveFolderId((p.product as any).hook_drive_folder_id ?? "");
      setBodyDriveFolderId((p.product as any).body_drive_folder_id ?? "");
      setTypes((p.product.types ?? []) as CreativeType[]);
      setBrandGuidelines((p.product as any).brand_guidelines ?? "");
      try { setOnboardingAnswers(JSON.parse((p.product as any).onboarding_json ?? "{}")); } catch { setOnboardingAnswers({}); }
      try { setVocabulary(JSON.parse((p.product as any).subtitle_vocabulary ?? "[]")); } catch { setVocabulary([]); }
      setSubtitlePreset((p.product as any).subtitle_preset ?? "");
      setSubtitleHighlightColor((p.product as any).subtitle_highlight_color ?? "");
      setSubtitlePosition((p.product as any).subtitle_position ?? "");
      if (c) setCosts(c);
      api.listFeatures(productId).then((r) => setFeatures(r.features ?? [])).catch(() => {});
      api.listCustomTypes(productId).then((r) => setCustomTypes(r.custom_types ?? [])).catch(() => {});
      api.listUGCScenarios(productId).then((r) => setUgcScenarios(r.ugc_scenarios ?? [])).catch(() => {});
    } catch (e) {
      console.error("ProductSettings reload error:", e);
      setLoadError(String(e));
    }
  }

  useEffect(() => {
    reload();
  }, [productId]);


  const [savingTypes, setSavingTypes] = useState(false);
  async function saveTypes(next: CreativeType[]) {
    setTypes(next);
    setSavingTypes(true);
    try {
      await api.updateProduct(productId, { types: next });
    } finally {
      setSavingTypes(false);
    }
  }

  async function saveDriveFolderId() {
    const trimmed = driveFolderId.trim();
    if (!trimmed) return;
    setSavingDrive(true);
    try {
      await api.updateProduct(productId, { drive_folder_id: trimmed });
      setProduct((p: any) => ({ ...p, drive_folder_id: trimmed }));
    } finally {
      setSavingDrive(false);
    }
  }

  async function saveHookDriveFolderId() {
    const trimmed = hookDriveFolderId.trim();
    if (!trimmed) return;
    setSavingHookDrive(true);
    try {
      await api.updateProduct(productId, { hook_drive_folder_id: trimmed });
      setProduct((p: any) => ({ ...p, hook_drive_folder_id: trimmed }));
    } finally {
      setSavingHookDrive(false);
    }
  }

  async function saveBodyDriveFolderId() {
    const trimmed = bodyDriveFolderId.trim();
    if (!trimmed) return;
    setSavingBodyDrive(true);
    try {
      await api.updateProduct(productId, { body_drive_folder_id: trimmed });
      setProduct((p: any) => ({ ...p, body_drive_folder_id: trimmed }));
    } finally {
      setSavingBodyDrive(false);
    }
  }

  async function addFeature() {
    const trimmed = newFeatureName.trim();
    if (!trimmed || features.some(f => f.name === trimmed)) return;
    const r = await api.addFeature(productId, trimmed);
    setFeatures(prev => [...prev, { id: r.id, name: trimmed, pain_points: [], audiences: [] }]);
    setNewFeatureName("");
    setExpandedFeature(r.id);
  }

  async function deleteFeature(featureId: string) {
    await api.deleteFeature(productId, featureId);
    setFeatures(prev => prev.filter(f => f.id !== featureId));
  }

  async function updateFeaturePainPoints(featureId: string, pain_points: string[]) {
    setFeatures(prev => prev.map(f => f.id === featureId ? { ...f, pain_points } : f));
    await api.updateFeature(productId, featureId, { pain_points });
  }

  async function updateFeatureAudiences(featureId: string, audiences: string[]) {
    setFeatures(prev => prev.map(f => f.id === featureId ? { ...f, audiences } : f));
    await api.updateFeature(productId, featureId, { audiences });
  }

  async function doDelete() {
    setDeleting(true);
    try {
      await api.deleteProduct(productId);
      window.dispatchEvent(new Event("products:changed"));
      navigate("/");
    } finally {
      setDeleting(false);
    }
  }

  if (!product) return <div className="p-8 text-zinc-500">{loadError ? `Hata: ${loadError}` : "Yükleniyor…"}</div>;

  return (
    <div className="p-8 max-w-2xl space-y-6">
      <section className="bg-white border rounded p-5">
        <h3 className="font-bold mb-3">Ürün Bilgisi</h3>
        <div className="text-sm space-y-3">
          <div>
            <span className="text-zinc-500">Ad: </span>
            {product.name}
          </div>
          <div>
            <span className="text-zinc-500">Tag: </span>
            <span className="font-mono">{product.tag}</span>
          </div>
          <div>
            <div className="text-zinc-500 mb-1">Drive Klasör ID:</div>
            <div className="flex gap-2 items-center">
              <input
                value={driveFolderId}
                onChange={(e) => setDriveFolderId(e.target.value)}
                placeholder="Google Drive klasör ID'si"
                className="flex-1 border rounded px-2 py-1.5 text-xs font-mono"
                onKeyDown={(e) => e.key === "Enter" && saveDriveFolderId()}
              />
              <button
                onClick={saveDriveFolderId}
                disabled={savingDrive || !driveFolderId.trim() || driveFolderId.trim() === product.drive_folder_id}
                className="px-3 py-1.5 rounded bg-accent text-white text-xs disabled:opacity-50"
              >
                {savingDrive ? "Kaydediliyor…" : "Kaydet"}
              </button>
              {driveFolderId.trim() && (
                <a
                  href={`https://drive.google.com/drive/folders/${driveFolderId.trim()}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-blue-600 hover:underline whitespace-nowrap"
                >
                  Aç ↗
                </a>
              )}
            </div>
            <p className="text-xs text-zinc-400 mt-1">
              URL'deki son kısım: drive.google.com/drive/folders/<strong>bu_id</strong>
            </p>
          </div>

          <div className="pt-3 border-t">
            <div className="text-zinc-500 mb-1">🪝 Hook Asset'leri Drive Klasörü:</div>
            <div className="flex gap-2 items-center">
              <input
                value={hookDriveFolderId}
                onChange={(e) => setHookDriveFolderId(e.target.value)}
                placeholder="Hook video havuzu — Drive klasör ID'si"
                className="flex-1 border rounded px-2 py-1.5 text-xs font-mono"
                onKeyDown={(e) => e.key === "Enter" && saveHookDriveFolderId()}
              />
              <button
                onClick={saveHookDriveFolderId}
                disabled={savingHookDrive || !hookDriveFolderId.trim() || hookDriveFolderId.trim() === (product as any).hook_drive_folder_id}
                className="px-3 py-1.5 rounded bg-accent text-white text-xs disabled:opacity-50"
              >
                {savingHookDrive ? "Kaydediliyor…" : "Kaydet"}
              </button>
            </div>
            <p className="text-xs text-zinc-400 mt-1">
              İsteğe bağlı: içinde feature adında alt klasörler varsa (örn. "ADHD"), Hook Body Avatar üretiminde önce oradan seçilir.
            </p>
          </div>

          <div className="pt-3 border-t">
            <div className="text-zinc-500 mb-1">🎬 Body / Packshot Asset'leri Drive Klasörü:</div>
            <div className="flex gap-2 items-center">
              <input
                value={bodyDriveFolderId}
                onChange={(e) => setBodyDriveFolderId(e.target.value)}
                placeholder="İçinde 'Body' ve 'Packshot' alt klasörleri olan Drive klasör ID'si"
                className="flex-1 border rounded px-2 py-1.5 text-xs font-mono"
                onKeyDown={(e) => e.key === "Enter" && saveBodyDriveFolderId()}
              />
              <button
                onClick={saveBodyDriveFolderId}
                disabled={savingBodyDrive || !bodyDriveFolderId.trim() || bodyDriveFolderId.trim() === (product as any).body_drive_folder_id}
                className="px-3 py-1.5 rounded bg-accent text-white text-xs disabled:opacity-50"
              >
                {savingBodyDrive ? "Kaydediliyor…" : "Kaydet"}
              </button>
            </div>
            <p className="text-xs text-zinc-400 mt-1">
              Bu klasörün içinde tam olarak <span className="font-mono">Body</span> ve <span className="font-mono">Packshot</span> adında iki alt klasör olmalı. Önizlemeleri Asset Library sayfasından görebilirsin.
            </p>
          </div>
        </div>
      </section>

      <CompactTreeSection productId={productId} />

      {/* ── Brand Guidelines ── */}
      <section className="bg-white border rounded p-5">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-bold">Brand Guidelines</h3>
          {(savingBrand || generatingBrand) && <span className="text-xs text-zinc-400">{generatingBrand ? "oluşturuluyor…" : "kaydediliyor…"}</span>}
        </div>
        <p className="text-xs text-zinc-500 mb-4">
          Cevaplar tüm reklam scriptleri ve görsel promptlarına enjekte edilir. Opsiyonel sorulara "yok" veya boş bırakabilirsin.
        </p>

        <div className="space-y-2">
          {BRAND_QUESTIONS.map((q) => {
            const val = onboardingAnswers[q.key] ?? "";
            const isOpen = expandedBrandQ === q.key;
            const hasAnswer = val.trim().length > 0;
            return (
              <div key={q.key} className="border rounded-lg overflow-hidden">
                <button
                  className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-zinc-50 transition-colors"
                  onClick={() => setExpandedBrandQ(isOpen ? null : q.key)}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${hasAnswer ? "bg-emerald-400" : "bg-zinc-200"}`} />
                    <span className="text-sm font-medium text-zinc-800 truncate">{q.label}</span>
                  </div>
                  <span className="text-zinc-400 text-xs shrink-0 ml-2">{isOpen ? "▲" : "▼"}</span>
                </button>
                {isOpen && (
                  <div className="px-4 pb-3 border-t bg-zinc-50">
                    <textarea
                      autoFocus
                      rows={3}
                      className="w-full mt-2 border rounded px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-accent/40 bg-white"
                      placeholder={q.placeholder}
                      value={val}
                      onChange={(e) => setOnboardingAnswers((prev) => ({ ...prev, [q.key]: e.target.value }))}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button
            disabled={savingBrand || generatingBrand}
            onClick={async () => {
              setSavingBrand(true);
              try {
                await api.updateProduct(productId, { onboarding_json: JSON.stringify(onboardingAnswers) });
              } finally { setSavingBrand(false); }
            }}
            className="px-4 py-1.5 rounded border text-sm disabled:opacity-50"
          >
            Cevapları Kaydet
          </button>
          <button
            disabled={savingBrand || generatingBrand}
            onClick={async () => {
              setGeneratingBrand(true);
              try {
                const data = await api.onboardProduct(productId, onboardingAnswers);
                if (data.ok) setBrandGuidelines(data.brand_guidelines ?? "");
                else alert("Hata: brand guidelines oluşturulamadı");
              } catch (e) {
                alert(`Hata: ${e}`);
              } finally { setGeneratingBrand(false); }
            }}
            className="px-4 py-1.5 rounded bg-accent text-white text-sm disabled:opacity-50"
          >
            {generatingBrand ? "Oluşturuluyor…" : "Brand Guidelines Oluştur / Güncelle"}
          </button>
        </div>

        <div className="mt-4">
          <p className="text-xs font-medium text-zinc-500 mb-1">Brand Guidelines (manuel düzenlenebilir):</p>
          <textarea
            className="w-full text-xs bg-zinc-50 border rounded p-3 text-zinc-700 h-48 resize-y font-mono"
            value={brandGuidelines}
            onChange={(e) => setBrandGuidelines(e.target.value)}
            placeholder="Henüz oluşturulmadı. Sağdaki butonla oluşturabilir veya buraya manuel yazabilirsin."
          />
          <div className="flex justify-end mt-1">
            <button
              disabled={savingBrand}
              onClick={async () => {
                setSavingBrand(true);
                try {
                  await api.updateProduct(productId, { brand_guidelines: brandGuidelines });
                } catch (e) { alert("Kaydedilemedi: " + (e as Error).message); }
                finally { setSavingBrand(false); }
              }}
              className="text-xs bg-zinc-800 text-white px-3 py-1 rounded hover:bg-zinc-700 disabled:opacity-50"
            >{savingBrand ? "Kaydediliyor…" : "Kaydet"}</button>
          </div>
        </div>
      </section>

      {/* ── Altyazı Ayarları ── */}
      <section className="bg-white border rounded p-5">
        <h3 className="font-bold mb-3">Altyazı Ayarları</h3>
        <div className="flex gap-4 mb-4">
          <div className="flex-1">
            <label className="text-xs text-zinc-500 block mb-1">Preset</label>
            <input
              className="border rounded px-2 py-1 text-sm w-full"
              placeholder="fusion"
              value={subtitlePreset}
              onChange={(e) => setSubtitlePreset(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs text-zinc-500 block mb-1">Highlight Color</label>
            <div className="flex gap-2 items-center">
              <input
                type="color"
                className="w-8 h-8 rounded border cursor-pointer"
                value={subtitleHighlightColor || "#ffffff"}
                onChange={(e) => setSubtitleHighlightColor(e.target.value)}
              />
              <input
                className="border rounded px-2 py-1 text-sm w-24"
                placeholder="#ffffff"
                value={subtitleHighlightColor}
                onChange={(e) => setSubtitleHighlightColor(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-zinc-500 block mb-1">Position</label>
            <select
              className="border rounded px-2 py-1 text-sm"
              value={subtitlePosition}
              onChange={(e) => setSubtitlePosition(e.target.value)}
            >
              <option value="">default</option>
              <option value="bottom">bottom</option>
              <option value="top">top</option>
              <option value="middle">middle</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={async () => {
                try {
                  await api.updateProduct(productId, { subtitle_preset: subtitlePreset || undefined, subtitle_highlight_color: subtitleHighlightColor || undefined, subtitle_position: subtitlePosition || undefined });
                } catch (e) { alert("Kaydedilemedi: " + (e as Error).message); }
              }}
              className="text-xs bg-zinc-800 text-white px-3 py-1 rounded hover:bg-zinc-700"
            >Kaydet</button>
          </div>
        </div>
        <h4 className="font-semibold text-sm mb-2">Düzeltme Kuralları</h4>
        <p className="text-xs text-zinc-500 mb-3">AI altyazı yanlış yazdığında otomatik düzeltme kuralları.</p>
        {vocabulary.map((entry, i) => (
          <div key={i} className="flex gap-2 items-center mb-2">
            <input
              className="border rounded px-2 py-1 text-sm w-32"
              placeholder="Doğru yazım"
              value={entry.word}
              onChange={(e) => {
                const next = vocabulary.map((v, j) => j === i ? { word: e.target.value, replaces: v.replaces } : v);
                setVocabulary(next);
              }}
            />
            <span className="text-zinc-400 text-xs">yerine</span>
            <input
              className="border rounded px-2 py-1 text-sm flex-1"
              placeholder="Yanlış yazımlar (virgülle ayır)"
              value={entry.replaces.join(", ")}
              onChange={(e) => {
                const next = vocabulary.map((v, j) => j === i ? { word: v.word, replaces: e.target.value.split(",").map((s: string) => s.trim()).filter(Boolean) } : v);
                setVocabulary(next);
              }}
            />
            <button
              onClick={() => setVocabulary(vocabulary.filter((_, j) => j !== i))}
              className="text-red-400 hover:text-red-600 text-xs px-2"
            >✕</button>
          </div>
        ))}
        <div className="flex gap-2 mt-2">
          <button
            onClick={() => setVocabulary([...vocabulary, { word: "", replaces: [] }])}
            className="text-xs text-blue-600 hover:underline"
          >+ Kural Ekle</button>
          <button
            disabled={savingVocab}
            onClick={async () => {
              setSavingVocab(true);
              try {
                await api.updateProduct(productId, { subtitle_vocabulary: JSON.stringify(vocabulary) });
              } catch (e) { alert("Kaydedilemedi: " + (e as Error).message); }
              finally { setSavingVocab(false); }
            }}
            className="ml-auto text-xs bg-zinc-800 text-white px-3 py-1 rounded hover:bg-zinc-700 disabled:opacity-50"
          >{savingVocab ? "Kaydediliyor…" : "Kaydet"}</button>
        </div>
      </section>

      {/* Senaryo alt kırılım editörü */}
      {scenarioEditor && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setScenarioEditor(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-md w-full p-5 flex flex-col gap-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <h4 className="font-semibold text-zinc-800">⚙ {scenarioEditor.name}</h4>
                <p className="text-xs text-zinc-500 mt-0.5">
                  Alt kırılımlar creative genome'a yazılır — performans analizi bu değişkenlere göre kırılır.
                </p>
              </div>
              <button onClick={() => setScenarioEditor(null)} className="text-zinc-400 hover:text-zinc-600 text-lg leading-none shrink-0">×</button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {([
                ["location", "Location", "airport, hospital…"],
                ["environment", "Environment", "travel selfie, clinical…"],
                ["music", "Music", "trending bgm, none…"],
                ["cam_angle", "Cam Angle", "selfie POV, wide…"],
                ["cam_movement", "Cam Movement", "handheld, static…"],
                ["emotion", "Emotion", "excited, reassuring…"],
                ["role_archetype", "Role Archetype", "doctor, traveler…"],
                ["season", "Season", "summer, winter…"],
              ] as [string, string, string][]).map(([key, label, ph]) => (
                <label key={key} className="text-xs text-zinc-600">
                  {label}
                  <input
                    value={scenarioDraft[key] ?? ""}
                    onChange={(e) => setScenarioDraft((d) => ({ ...d, [key]: e.target.value }))}
                    placeholder={ph}
                    className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
                  />
                </label>
              ))}
            </div>
            <button
              disabled={savingScenario}
              onClick={async () => {
                setSavingScenario(true);
                try {
                  const r = await api.updateUGCScenario(productId, scenarioEditor.id, scenarioDraft);
                  setUgcScenarios((prev) => prev.map((x) => x.id === scenarioEditor.id ? { ...x, ...(r.ugc_scenario as any) } : x));
                  setScenarioEditor(null);
                } catch { /* toast handled */ }
                setSavingScenario(false);
              }}
              className="self-end px-4 py-2 bg-accent text-white rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {savingScenario ? "Kaydediliyor…" : "Kaydet"}
            </button>
          </div>
        </div>
      )}

      {/* Prompt popup */}
      {promptPopup && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setPromptPopup(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-lg w-full p-5 flex flex-col gap-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-2">
              <h4 className="font-semibold text-zinc-800">{promptPopup.name}</h4>
              <button onClick={() => setPromptPopup(null)} className="text-zinc-400 hover:text-zinc-600 text-lg leading-none shrink-0">×</button>
            </div>
            <pre className="text-xs text-zinc-600 whitespace-pre-wrap bg-zinc-50 rounded-lg p-3 max-h-80 overflow-y-auto font-mono leading-relaxed">{promptPopup.prompt}</pre>
          </div>
        </div>
      )}

      <section className="bg-white border rounded p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold">Aktif Type Seti</h3>
          {savingTypes && <span className="text-xs text-zinc-500">kaydediliyor…</span>}
        </div>

        {/* Görsel types — image-kind only, never mixed with video */}
        {(types.length > 0 || imageCustomTypes.length > 0) && (
          <div className="mb-4">
            <p className="text-xs font-medium text-zinc-400 uppercase tracking-wide mb-2">Görsel</p>
            <div className="flex flex-wrap gap-2">
              {types.map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5 px-3 py-1 text-sm rounded border bg-accent text-white border-accent">
                  <button
                    className="hover:underline"
                    onClick={async () => {
                      setPromptPopup({ name: t, prompt: "Yükleniyor…" });
                      const r = await api.getTypeSystemPrompt(productId, t).catch(() => null);
                      setPromptPopup({ name: t, prompt: r?.system_prompt ?? "Prompt yüklenemedi." });
                    }}
                  >{t}</button>
                  <button
                    disabled={savingTypes}
                    onClick={() => saveTypes(types.filter((x) => x !== t))}
                    className="opacity-70 hover:opacity-100 leading-none disabled:opacity-30"
                  >×</button>
                </span>
              ))}
              {imageCustomTypes.map((ct) => (
                <span key={ct.id} className="inline-flex items-center gap-2 pl-1.5 pr-2 py-1 text-sm rounded border bg-accent text-white border-accent">
                  {ct.example_drive_file_id && (
                    <img
                      src={`${API_ORIGIN}/proxy/img/${ct.example_drive_file_id}`}
                      alt={ct.name}
                      className="w-6 h-6 rounded object-cover border border-white/30 shrink-0"
                    />
                  )}
                  <button
                    className="hover:underline"
                    onClick={() => setPromptPopup({ name: ct.name, prompt: ct.system_prompt ?? "Prompt yok." })}
                  >{ct.name}</button>
                  <button
                    onClick={async () => {
                      await api.deleteCustomType(productId, ct.id);
                      setCustomTypes((prev) => prev.filter((x) => x.id !== ct.id));
                    }}
                    className="opacity-70 hover:opacity-100 leading-none"
                  >×</button>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Video şablonları — Creative Builder'da "Video Tipi Olarak Kaydet" ile oluşturulanlar */}
        {videoCustomTypes.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-medium text-zinc-400 uppercase tracking-wide mb-2">Video Şablonları</p>
            <div className="flex flex-wrap gap-2">
              {videoCustomTypes.map((ct) => (
                <span key={ct.id} className="inline-flex items-center gap-2 pl-1.5 pr-2 py-1 text-sm rounded border bg-blue-600 text-white border-blue-600">
                  <button
                    className="hover:underline"
                    onClick={() => setPromptPopup({ name: ct.name, prompt: ct.system_prompt ?? "Prompt yok." })}
                  >🎬 {ct.name}</button>
                  <button
                    onClick={async () => {
                      await api.deleteCustomType(productId, ct.id);
                      setCustomTypes((prev) => prev.filter((x) => x.id !== ct.id));
                    }}
                    className="opacity-70 hover:opacity-100 leading-none"
                  >×</button>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* UGC senaryoları — Panel/Influencer POV/Podcast/Doctor tarzı script'li video akışları */}
        {ugcScenarios.length > 0 && (
          <div>
            <p className="text-xs font-medium text-zinc-400 uppercase tracking-wide mb-2">UGC Senaryoları</p>
            <div className="flex flex-wrap gap-2">
              {ugcScenarios.map((sc) => (
                <span key={sc.id} className="inline-flex items-center gap-1.5 px-3 py-1 text-sm rounded border bg-violet-600 text-white border-violet-600">
                  <button
                    className="hover:underline"
                    onClick={() => setPromptPopup({ name: sc.name, prompt: sc.description })}
                  >{sc.name}</button>
                  <button
                    title="Alt kırılımları düzenle (location, music, cam angle…)"
                    onClick={() => {
                      setScenarioEditor(sc);
                      setScenarioDraft({
                        location: sc.location ?? "",
                        environment: sc.environment ?? "",
                        music: sc.music ?? "",
                        cam_angle: sc.cam_angle ?? "",
                        cam_movement: sc.cam_movement ?? "",
                        emotion: sc.emotion ?? "",
                        role_archetype: sc.role_archetype ?? "",
                        season: sc.season ?? "",
                      });
                    }}
                    className="opacity-70 hover:opacity-100 leading-none"
                  >⚙</button>
                  <button
                    onClick={async () => {
                      await api.deleteUGCScenario(productId, sc.id);
                      setUgcScenarios((prev) => prev.filter((x) => x.id !== sc.id));
                    }}
                    className="opacity-70 hover:opacity-100 leading-none"
                  >×</button>
                </span>
              ))}
            </div>
          </div>
        )}

        {types.length === 0 && customTypes.length === 0 && ugcScenarios.length === 0 && (
          <p className="text-xs text-zinc-400">Henüz aktif type yok. Creative Builder'dan görsel/video tipi oluşturabilirsin.</p>
        )}
      </section>

      <section className="bg-white border rounded p-5">
        <div className="flex items-baseline justify-between mb-4">
          <h3 className="font-bold">Maliyet Özeti</h3>
          {costs && (
            <span className="text-sm text-zinc-500">
              Toplam{" "}
              <span className="font-mono font-semibold text-zinc-800">
                ${costs.grand_total_usd.toFixed(2)}
              </span>
            </span>
          )}
        </div>
        {!costs ? (
          <div className="text-sm text-zinc-400">Yükleniyor…</div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              {[
                { key: "image",    label: "Image Creation",    icon: "🖼️" },
                { key: "video",    label: "Video Creation",    icon: "🎬" },
                { key: "playable", label: "Playable Creation", icon: "🎮" },
              ].map(({ key, label, icon }) => {
                const p = costs.pillars[key] ?? { count: 0, unit: "adet", cost_usd: 0 };
                const isEmpty = p.count === 0 && p.cost_usd === 0;
                return (
                  <div
                    key={key}
                    className={`rounded-lg border p-4 flex flex-col gap-2 ${
                      isEmpty ? "bg-zinc-50 border-zinc-100" : "bg-white border-zinc-200"
                    }`}
                  >
                    <div className="text-2xl">{icon}</div>
                    <div className="text-xs font-medium text-zinc-500">{label}</div>
                    {isEmpty ? (
                      <div className="text-xs text-zinc-300 mt-auto">Henüz yok</div>
                    ) : (
                      <>
                        <div className="text-2xl font-bold text-zinc-900 leading-none">
                          {p.count.toLocaleString()}
                          <span className="text-sm font-normal text-zinc-400 ml-1">{p.unit}</span>
                        </div>
                        <div className="text-xs font-mono text-zinc-500 mt-auto">
                          ${p.cost_usd.toFixed(2)}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Language breakdown */}
            {costs.lang_breakdown && Object.keys(costs.lang_breakdown).length > 0 && (
              <div className="rounded-lg border border-zinc-100 bg-zinc-50 p-4">
                <div className="text-xs font-medium text-zinc-500 mb-3">Dil Kırılımı</div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(costs.lang_breakdown)
                    .sort(([, a], [, b]) => b - a)
                    .map(([lang, cnt]) => (
                      <div key={lang} className="flex items-center gap-1.5 bg-white border border-zinc-200 rounded-lg px-3 py-1.5">
                        <span className="text-xs font-mono font-semibold text-zinc-700">{lang}</span>
                        <span className="text-xs text-zinc-400">{cnt.toLocaleString()} dosya</span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      <section className="bg-white border border-red-200 rounded p-5">
        <h3 className="font-bold mb-1 text-red-700">Tehlikeli bölge</h3>
        <p className="text-xs text-zinc-600 mb-3">
          Bu işlem ürünü, tüm feature'larını, jobs/medya kayıtlarını ve cost
          geçmişini geri alınamaz şekilde siler. Drive'daki dosyalara dokunmaz.
        </p>
        {!confirmDelete ? (
          <button
            onClick={() => setConfirmDelete(true)}
            className="px-3 py-1.5 rounded border border-red-300 text-red-700 text-sm hover:bg-red-50"
          >
            Ürünü sil…
          </button>
        ) : (
          <div className="space-y-2">
            <div className="text-sm text-red-700">
              Emin misin? <span className="font-mono">{product.tag}</span> ürünü ve
              tüm bağlı kayıtları silinecek.
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
                className="px-3 py-1.5 rounded border text-sm"
              >
                Vazgeç
              </button>
              <button
                onClick={doDelete}
                disabled={deleting}
                className="px-3 py-1.5 rounded bg-red-600 text-white text-sm disabled:opacity-50"
              >
                {deleting ? "Siliniyor…" : "Evet, sil"}
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

// ── FeatureAccordion ─────────────────────────────────────────────────────────
function FeatureAccordion({
  feature, expanded, onToggle, onDelete, onUpdatePainPoints, onUpdateAudiences,
}: {
  feature: { id: string; name: string; pain_points: string[]; audiences: string[] };
  expanded: boolean;
  onToggle: () => void;
  onDelete: () => void;
  onUpdatePainPoints: (pp: string[]) => void;
  onUpdateAudiences: (au: string[]) => void;
}) {
  const [newPP, setNewPP] = useState("");
  const [newAu, setNewAu] = useState("");

  return (
    <div className={`border rounded-lg overflow-hidden transition-all ${expanded ? "border-accent/40" : "border-zinc-200"}`}>
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-2.5 bg-zinc-50 cursor-pointer" onClick={onToggle}>
        <span className={`text-xs transition-transform ${expanded ? "rotate-90" : ""}`}>▶</span>
        <span className="flex-1 text-sm font-semibold text-zinc-800">{feature.name}</span>
        <span className="text-xs text-zinc-400 mr-2">
          {feature.pain_points.length} pain point · {feature.audiences.length} audience
        </span>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="text-xs text-red-500 hover:text-red-700 px-1"
        >
          sil
        </button>
      </div>

      {/* Body */}
      {expanded && (
        <div className="px-4 py-3 grid grid-cols-2 gap-4 border-t border-zinc-100">
          {/* Pain Points */}
          <div>
            <div className="text-xs font-semibold text-zinc-600 mb-2">Pain Points</div>
            <div className="flex gap-1.5 mb-2">
              <input
                value={newPP}
                onChange={(e) => setNewPP(e.target.value)}
                placeholder="Ekle..."
                className="flex-1 border rounded px-2 py-1 text-xs"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newPP.trim()) {
                    onUpdatePainPoints([...feature.pain_points, newPP.trim()]);
                    setNewPP("");
                  }
                }}
              />
              <button
                onClick={() => { if (newPP.trim()) { onUpdatePainPoints([...feature.pain_points, newPP.trim()]); setNewPP(""); } }}
                className="px-2 py-1 bg-accent text-white text-xs rounded"
              >+</button>
            </div>
            <div className="flex flex-wrap gap-1">
              {feature.pain_points.map((pp) => (
                <span key={pp} className="inline-flex items-center gap-1 bg-zinc-100 text-zinc-700 text-xs rounded px-2 py-0.5">
                  {pp}
                  <button onClick={() => onUpdatePainPoints(feature.pain_points.filter(x => x !== pp))} className="text-zinc-400 hover:text-red-500 leading-none">×</button>
                </span>
              ))}
              {feature.pain_points.length === 0 && <span className="text-xs text-zinc-400">Henüz yok</span>}
            </div>
          </div>

          {/* Audiences */}
          <div>
            <div className="text-xs font-semibold text-zinc-600 mb-2">Audiences</div>
            <div className="flex gap-1.5 mb-2">
              <input
                value={newAu}
                onChange={(e) => setNewAu(e.target.value)}
                placeholder="Ekle..."
                className="flex-1 border rounded px-2 py-1 text-xs"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newAu.trim()) {
                    onUpdateAudiences([...feature.audiences, newAu.trim()]);
                    setNewAu("");
                  }
                }}
              />
              <button
                onClick={() => { if (newAu.trim()) { onUpdateAudiences([...feature.audiences, newAu.trim()]); setNewAu(""); } }}
                className="px-2 py-1 bg-accent text-white text-xs rounded"
              >+</button>
            </div>
            <div className="flex flex-wrap gap-1">
              {feature.audiences.map((au) => (
                <span key={au} className="inline-flex items-center gap-1 bg-violet-50 text-violet-700 text-xs rounded px-2 py-0.5">
                  {au}
                  <button onClick={() => onUpdateAudiences(feature.audiences.filter(x => x !== au))} className="text-violet-300 hover:text-red-500 leading-none">×</button>
                </span>
              ))}
              {feature.audiences.length === 0 && <span className="text-xs text-zinc-400">Henüz yok</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
